/**
 * OrganizationList Component
 *
 * Displays a grid of available organizations for switching context.
 * Includes the main production database and dynamically loaded organizations.
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
    FiDatabase,
    FiShield,
    FiRefreshCw,
    FiCheck
} from 'react-icons/fi';

const OrganizationList = ({
    organizations,
    currentOrgId,
    loading,
    onRefresh,
    onSelectOrganization
}) => {
    return (
        <div className="dos-org-section">
            <div className="dos-section-header">
                <FiDatabase className="dos-section-icon" />
                <span className="dos-section-title">Available Organizations</span>
                <button
                    className="dos-refresh-button"
                    onClick={onRefresh}
                    disabled={loading}
                    title="Refresh Organizations"
                >
                    <FiRefreshCw className={loading ? 'dos-rotating' : ''} />
                </button>
            </div>

            <div className="dos-org-grid">
                {/* Main Production DB */}
                <div
                    className={`dos-org-card ${currentOrgId === 'main-prod-db' ? 'dos-active' : ''}`}
                    onClick={() => onSelectOrganization({
                        organization_id: 'main-prod-db',
                        name: 'Main Production Database',
                        subdomain: 'main',
                        status: 'production'
                    })}
                >
                    <div className="dos-org-card-header">
                        <FiShield className="dos-org-icon dos-prod-icon" />
                        <div className="dos-org-badge dos-prod-badge">PROD</div>
                    </div>
                    <div className="dos-org-card-content">
                        <h4 className="dos-org-card-title">Production Database</h4>
                        <p className="dos-org-card-url">clockwisecpa.app</p>
                        <p className="dos-org-card-description">Main production environment</p>
                    </div>
                    {currentOrgId === 'main-prod-db' && (
                        <div className="dos-active-indicator">
                            <FiCheck className="dos-check-icon" />
                        </div>
                    )}
                </div>

                {/* Dynamic Organizations */}
                {organizations.map(org => (
                    <div
                        key={org.organization_id}
                        className={`dos-org-card ${currentOrgId === org.organization_id ? 'dos-active' : ''}`}
                        onClick={() => onSelectOrganization(org)}
                    >
                        <div className="dos-org-card-header">
                            <FiDatabase className={`dos-org-icon ${
                                org.isLocalDev ? 'dos-dev-icon' : 'dos-org-icon-default'
                            }`} />
                            <div className={`dos-org-badge ${
                                org.isLocalDev ? 'dos-dev-badge' :
                                org.status === 'active' ? 'dos-active-badge' : 'dos-inactive-badge'
                            }`}>
                                {org.isLocalDev ? 'DEV' : org.status?.toUpperCase() || 'ORG'}
                            </div>
                        </div>
                        <div className="dos-org-card-content">
                            <h4 className="dos-org-card-title">{org.name}</h4>
                            <p className="dos-org-card-url">
                                {org.isLocalDev ? 'localhost:3000' : `${org.subdomain}.clockwisecpa.app`}
                            </p>
                            <p className="dos-org-card-description">
                                {org.isLocalDev ? 'Local development environment' :
                                 `Status: ${org.status || 'unknown'}`}
                            </p>
                        </div>
                        {currentOrgId === org.organization_id && (
                            <div className="dos-active-indicator">
                                <FiCheck className="dos-check-icon" />
                            </div>
                        )}
                    </div>
                ))}

                {loading && (
                    <div className="dos-loading-card">
                        <FiRefreshCw className="dos-loading-icon dos-rotating" />
                        <p>Loading organizations...</p>
                    </div>
                )}
            </div>
        </div>
    );
};

OrganizationList.propTypes = {
    organizations: PropTypes.arrayOf(PropTypes.shape({
        organization_id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        subdomain: PropTypes.string.isRequired,
        status: PropTypes.string,
        isLocalDev: PropTypes.bool
    })).isRequired,
    currentOrgId: PropTypes.string,
    loading: PropTypes.bool.isRequired,
    onRefresh: PropTypes.func.isRequired,
    onSelectOrganization: PropTypes.func.isRequired
};

export default OrganizationList;
